"use client";

import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: "How does revenue management improve hotel profitability?",
    answer: "Revenue management uses data-driven strategies to sell the right room to the right guest at the right time for the right price. By optimizing your OTA listings, implementing dynamic pricing, and monitoring market demand, we ensure your property maximizes RevPAR and overall profitability."
  }, {
    question: "What is the benefit of a professional revenue audit?",
    answer: "A revenue audit identifies hidden leaks in your current strategy. We analyze your tech stack, rate parity, and distribution channels to uncover opportunities for growth. Our audits provide actionable insights that help you capture more direct bookings and reduce dependency on high-commission OTAs."
  }, {
    question: "How do you handle OTA listing optimization?",
    answer: "We optimize your presence on platforms like Booking.com and Expedia by refining content, managing images, and adjusting rate strategies based on real-time market data. This improves your visibility, ranking, and conversion rates, ensuring your property stands out in a crowded marketplace."
  }, {
    question: "Can you help with hotel technology recommendations?",
    answer: "Yes. We evaluate your current tech stack—including PMS, Channel Managers, and Booking Engines—to ensure they are integrated and efficient. We recommend modern, scalable solutions that streamline operations and enhance the guest experience, ultimately driving more revenue."
  }, {
    question: "Do you provide ongoing revenue management support?",
    answer: "Absolutely. We offer flexible, long-term partnerships where we act as your remote revenue management team. From daily rate adjustments to monthly performance reporting, we provide the consistent oversight needed to keep your property competitive and profitable."
  }, {
    question: "Who owns the data and strategy insights?",
    answer: "You do. All strategies, reports, and data insights generated during our engagement belong to your property. We provide full transparency, ensuring you have complete control over your revenue management processes and the tools we implement."
  }, {
    question: "How do you work with hotels in different time zones?",
    answer: "As a global consultancy, we are accustomed to working across time zones. We utilize efficient communication tools and scheduled virtual meetings to ensure seamless collaboration, providing you with timely updates and support regardless of your location."
  }, {
    question: "What is the typical process for getting started?",
    answer: "We begin with a discovery call to understand your property's unique challenges. Following this, we conduct a comprehensive revenue audit. Once we identify the key growth areas, we develop a tailored strategy and begin implementation to start driving results immediately."
  }];
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  return <section data-section-id="3717" id="hotelseofaq" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 lg:px-12">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <p className="font-default text-xs uppercase tracking-widest mb-8" style={{
              color: "var(--secondary)"
            }}>
                FAQ — Revenue Insights
              </p>
              <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
                Expert
                <br />
                <span style={{
                color: "var(--secondary)"
              }}>Answers.</span>
              </h2>
              <p className="font-default text-foreground/70 text-base leading-relaxed mb-10 max-w-md">
                Common questions from hotel owners and managers about maximizing revenue, optimizing OTA performance, and leveraging modern hospitality technology.
              </p>
              <Link to="/ContactPage" className="inline-flex items-center gap-2 font-default text-sm uppercase tracking-wider text-foreground border-b border-foreground/30 pb-1 hover:border-foreground transition-colors duration-300">
                <span>Need specific advice?</span>
                <span style={{
                color: "var(--secondary)"
              }}>→</span>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="border-t border-border">
              {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              const num = String(i + 1).padStart(2, "0");
              return <div data-index={i} key={i} className="border-b border-border">
                    <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full flex items-start justify-between gap-6 py-8 text-left group cursor-pointer" aria-expanded={isOpen}>
                      <div className="flex items-start gap-6 md:gap-8 flex-1 min-w-0">
                        <span className={`font-default text-sm pt-2 shrink-0 transition-all duration-300 ${isOpen ? "opacity-100" : "opacity-70"}`} style={{
                      color: "var(--secondary)"
                    }}>
                          {num}.
                        </span>
                        <h3 className="font-default font-semibold text-xl md:text-2xl lg:text-[1.65rem] leading-snug tracking-tight text-foreground group-hover:text-foreground/90 transition-colors">
                          {faq.question}
                        </h3>
                      </div>
                      <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 md:w-11 md:h-11 rounded-lg border transition-all duration-300" style={{
                    borderColor: isOpen ? "var(--secondary)" : "var(--border)",
                    backgroundColor: isOpen ? "hsla(43, 65%, 35%, 0.1)" : "transparent",
                    color: "var(--secondary)"
                  }}>
                        <span className={`inline-flex transition-transform duration-400 ease-out ${isOpen ? "rotate-45" : "rotate-0"}`}>
                          {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </span>
                      </span>
                    </button>

                    <div className="grid transition-all duration-[400ms] ease-out" style={{
                  gridTemplateRows: isOpen ? "1fr" : "0fr"
                }}>
                      <div className="overflow-hidden">
                        <div className="pl-0 md:pl-14 pr-0 md:pr-14 pb-8 -mt-2">
                          <p className="font-default text-foreground/70 text-base md:text-[1.05rem] leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
    </section>;
}